import { Link } from "react-router";

import { Caution, HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { table, td, th } from "@/components/table";

const SECTIONS = [
  { id: "snapshot", title: "The directory snapshot" },
  { id: "directory", title: "Directory view" },
  { id: "ad", title: "Active Directory view" },
  { id: "scenarios", title: "Scenarios" },
  { id: "write", title: "What is written to Active Directory" },
  { id: "import", title: "Importing HR's workbook" },
  { id: "export", title: "Export and presentation" },
  { id: "single", title: "Changing one account" },
];

const LIFECYCLE: Array<[string, string]> = [
  ["draft", "Being prepared. Changes can be added, edited, removed."],
  ["approved", "Frozen. Can be tested against AD, or reopened."],
  ["testing", "The worker reads every target in AD and reports what it would do. Writes nothing."],
  ["applying", "The worker writes, reads back, and waits for Entra to show the value."],
  ["applied", "Every change verified in the directory."],
  ["partial", "Nothing left to try and something did not land. The failed changes can be retried."],
  [
    "archived",
    "Out of the list, kept as the record. A scenario that wrote to AD is never deleted.",
  ],
];

export function OrgHelpPage() {
  return (
    <HelpLayout
      title="Organisation"
      intro="The reporting structure as the directory has it, and what a target structure from HR would change in it. Scenarios are prepared, approved, tested and applied; the write goes to on-prem Active Directory and shows in Entra after the next sync."
      sections={SECTIONS}
    >
      <HelpSectionBlock id="snapshot" title="The directory snapshot">
        <p>
          Everything on the Organisation page works on a frozen copy of the directory. HROps reads
          Entra ID every 15 minutes and keeps a new copy only when something changed; Refresh
          directory reads it at once. A scenario is a difference against one snapshot, so the
          baseline has to stand still while people work on it: the scenario keeps the copy it was
          built on. A stale baseline shows up later as &quot;drift&quot; on changes whose current
          value has moved.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="directory" title="Directory view">
        <p>
          The reporting lines today, as a left-to-right tree. Mailboxes, scanners and admin accounts
          are hidden by default and can be shown. Search for a person to centre on them; zoom and
          pan with the mouse or the keyboard.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="ad" title="Active Directory view">
        <p>
          One table of every person&apos;s work fields as the directory holds them: supervisor, job
          title, department, area, country, city, legal entity and office (brand), plus whether the
          account is enabled and whether it comes from on-prem AD. Search covers name, sign-in name,
          title, department, supervisor and city, and ignores case and accents. Filter by legal
          entity, country, city, department, area or supervisor; every option says how many people
          it holds, and the spellings are the directory&apos;s own, so a misspelt legal entity can
          be found. <em>Gaps only</em> shows the people missing a supervisor, job title, department,
          country, city or legal entity, or reporting to a disabled account. A missing area is left
          out of it, because about a third of people have none yet: the Area filter&apos;s
          &quot;(empty)&quot; finds those. The address bar keeps the list, so a link to it shows a
          colleague the same rows.
        </p>
        <p>
          <strong>Area</strong> is the business unit, AD&apos;s <code>businessCategory</code>. Entra
          Connect has copied it into Entra ID (<code>extensionAttribute15</code>) since 6 Oct 2026,
          and the view reads it from there.
        </p>
        <p>
          <strong>How fresh.</strong> The view shows what Entra ID holds. A change made in AD
          arrives there with the next Entra Connect sync, about 30 minutes, and HROps reads Entra ID
          every 15 minutes, so the table can trail AD by up to about 45 minutes. &quot;Entra ID as
          of&quot; is the last read; its dot turns amber after 30 minutes and red after two hours.
          Refresh reads it at once.
        </p>
        <p>
          <strong>Export to Excel</strong> writes exactly the rows and columns on screen, in the
          same order, with a second sheet saying when the copy was taken and how it was filtered.
          Every export is logged.
        </p>
        <Caution>
          The view is read-only. A wrong value is corrected on the{" "}
          <Link className="underline" to="/workforce?view=report">
            Workforce page
          </Link>
          : Report a change, which is approved there and then written to AD. The person&apos;s
          drawer in the view opens that form with the person filled in.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="scenarios" title="Scenarios">
        <p>
          A scenario is a target structure for many people: new managers, titles, departments,
          business units, legal entities, countries, cities. It is built by importing HR&apos;s
          workbook or by hand from the chart, simulated (cycles, orphans, managers who are leaving,
          span outliers), drawn before and after, and then taken through the lifecycle below.
        </p>
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Status</th>
              <th className={th}>Means</th>
            </tr>
          </thead>
          <tbody>
            {LIFECYCLE.map(([s, m]) => (
              <tr key={s}>
                <td className={`${td} font-medium whitespace-nowrap`}>{s}</td>
                <td className={`${td} text-fg-muted`}>{m}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2">
          Every HROps user may prepare, approve and export a scenario. Testing against AD and
          applying are limited to a few named people, because a scenario touches hundreds of
          accounts at once.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="write" title="What is written to Active Directory">
        <p>
          Manager, job title, department, business unit, company, office, country and city, on the
          person&apos;s account in on-prem AD, through a service account that may write exactly
          those attributes and nothing else. Each change is refused if the attribute has changed
          since the snapshot (drift), if the account is a protected administrator, or if the person
          has no on-prem account. Each write is read back from the domain controller and marked
          verified only when Entra shows the value, after the sync cycle.
        </p>
        <Caution>
          Undo exists for what actually landed: it prepares the reverse as a new scenario, which is
          approved and applied like any other. An attribute that was empty before cannot be put back
          to empty from here.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="import" title="Importing HR's workbook">
        <p>
          Two layouts are accepted: HR&apos;s own name-keyed file (Name, L2 Area, Current and Future
          Manager, titles, departments) and the HROps template with sign-in names. Names are
          resolved against the snapshot; where a name fits several people, or nobody, the import
          asks before anything is created. A blank cell always means &quot;no change&quot;, never
          &quot;clear&quot;.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="export" title="Export and presentation">
        <p>
          A scenario exports as the HROps workbook (re-importable) and as a PowerShell script with
          every change visible, for whoever changes AD by hand. The chart exports as PDF, PNG and
          PowerPoint for a presentation.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="single" title="Changing one account">
        <p>
          A change to one person&apos;s supervisor, title, department, area, country, city or legal
          entity is reported on the{" "}
          <Link className="underline" to="/workforce">
            Workforce page
          </Link>{" "}
          and written to AD when it is approved there, through the same path a scenario uses. The
          earlier self-service &quot;Change an account&quot; tab, which wrote on the spot without a
          second person, is switched off.
        </p>
      </HelpSectionBlock>
    </HelpLayout>
  );
}
